/**
 * 誌面の仕掛けをポップアップで出す共通処理（EP-2・T012・D178）。
 *
 * 中身を持つ仕掛け（video / audio / form）は、payload の `display` で
 * 「誌面に埋め込む（既定）」か「誌面に小さなマーカーを置き、クリックでポップアップを開いて
 * その中に出す」かを選べる。`media_note` は後者の形を常にとる種別（M7b から）で、
 * ここはその作りを種別をまたいで使えるように切り出したもの。
 *
 * ポップアップは初回オープン時に 1 度だけ作り、以後は表示/非表示を切り替える
 * （中身を作り直さない＝再生位置・入力した値が残る）。ページから外れたら閉じる。
 */
import { openDialog } from '../../a11y/dialog';
import { cls } from '../../brand';
import { ICONS } from '../../ui/icons';
import type { OverlayContext, OverlayItem, OverlayRenderer } from '../registry';

/**
 * 表示方法で描き手を振り分ける。`display: 'popup'` のときだけ popup の描き手へ渡し、
 * 省略・`inline`・未知の値は埋め込みの描き手のまま（サーバは `popup` しか保存しない）。
 */
export function byDisplay(inline: OverlayRenderer, popup: OverlayRenderer): OverlayRenderer {
  return (el, item, context) =>
    item.payload['display'] === 'popup' ? popup(el, item, context) : inline(el, item, context);
}

/** マーカー＋ポップアップの描き手を組み立てるための指定。 */
export interface MarkerPopupSpec {
  /** マーカーのクラス（cls 前）。中のアイコンは `<markerClass>-icon`。 */
  markerClass: string;
  /** label が無いときのマーカー（とダイアログ）の名前の文言キー。 */
  labelKey: string;
  /** パネルへ足すクラス（cls 前）。中身に合わせた寸法・地の色を当てる。 */
  panelClass?: string;
  /**
   * 初めて開くときに 1 度だけ呼ばれ、ステージへ中身を描く。
   * 閉じるたびに呼ぶ処理（再生の一時停止など）があれば返す。
   */
  fill(stage: HTMLElement, item: OverlayItem, context: OverlayContext): (() => void) | void;
}

/** 誌面にマーカーの `<button>` を置き、クリックでポップアップを開く描き手。 */
export function markerPopupRenderer(spec: MarkerPopupSpec): OverlayRenderer {
  return (el, item, context) => {
    const doc = context.doc;
    const payload = item.payload as { label?: string };
    // 開いたボタンとダイアログの名前を一致させる（作者の label、無ければ種別の文言）
    const label = payload.label ?? context.translate(spec.labelKey);

    const marker = doc.createElement('button');
    marker.type = 'button';
    marker.className = cls(spec.markerClass);
    marker.setAttribute('aria-label', label);
    const icon = doc.createElement('span');
    icon.className = cls(`${spec.markerClass}-icon`);
    icon.setAttribute('aria-hidden', 'true');
    marker.appendChild(icon);
    el.appendChild(marker);

    let popup: OverlayPopup | null = null;
    marker.addEventListener('click', () => {
      if (popup === null) {
        let onClose: (() => void) | null = null;
        popup = createOverlayPopup(doc, context, {
          label,
          ...(spec.panelClass !== undefined ? { panelClass: spec.panelClass } : {}),
          onClose: () => onClose?.(),
        });
        const filled = spec.fill(popup.stage, item, context);
        if (typeof filled === 'function') onClose = filled;
      }
      popup.open();
    });

    return {
      dispose: () => popup?.destroy(),
      // ページから外れたら閉じる（音が鳴り続けない・入力した値は残す）
      visibilityChanged: (visible) => {
        if (!visible) popup?.close();
      },
    };
  };
}

export interface OverlayPopup {
  /** 中身を描く場所。 */
  readonly stage: HTMLElement;
  open(): void;
  close(): void;
  destroy(): void;
}

interface PopupOptions {
  /** ダイアログの名前。 */
  label: string;
  panelClass?: string;
  /** 閉じたときに呼ぶ。 */
  onClose?: () => void;
}

/**
 * ポップアップ（誌面の上に浮くパネル）。オーバーレイ層の外＝`doc.body` 直下に出して
 * 層のクリップ・スタッキングから逃がす。Esc・背景クリック・× で閉じる。
 */
export function createOverlayPopup(doc: Document, context: OverlayContext, options: PopupOptions): OverlayPopup {
  const backdrop = doc.createElement('div');
  backdrop.className = cls('overlay-note-backdrop');
  backdrop.hidden = true;

  const panel = doc.createElement('div');
  panel.className = cls('overlay-note-panel');
  if (options.panelClass !== undefined) panel.classList.add(cls(options.panelClass));
  backdrop.appendChild(panel);

  const closeButton = doc.createElement('button');
  closeButton.type = 'button';
  closeButton.className = cls('overlay-note-close');
  closeButton.setAttribute('aria-label', context.translate('overlay.close'));
  // 可視のラベルは持たせない（アイコンは aria-hidden）。「×」の文字を出すと、
  // 見えている文字と読み上げ名が食い違う（WCAG 2.5.3 Label in Name）。
  closeButton.innerHTML = ICONS.close ?? '';
  panel.appendChild(closeButton);

  const stage = doc.createElement('div');
  stage.className = cls('overlay-note-stage');
  panel.appendChild(stage);

  doc.body.appendChild(backdrop);

  // 役割・モーダル・名前・フォーカスの移動と復帰・Tab の閉じ込め・Esc は共通処理へ。
  let releaseDialog: (() => void) | null = null;

  function open(): void {
    if (!backdrop.hidden) return;
    backdrop.hidden = false;
    releaseDialog = openDialog(panel, { label: options.label, onEscape: () => close() });
  }

  function close(): void {
    if (backdrop.hidden) return;
    backdrop.hidden = true;
    releaseDialog?.();
    releaseDialog = null;
    options.onClose?.();
  }

  closeButton.addEventListener('click', () => close());
  backdrop.addEventListener('click', (event) => {
    if (event.target === backdrop) close();
  });

  return {
    stage,
    open,
    close,
    destroy: () => {
      close();
      backdrop.remove();
    },
  };
}
